import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Plus, Minus, Trash2, ShoppingCart, PackageCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { adminOrderApi, type StoreProduct, type ProductDetail } from '@/api/admin-order'
import { promotionBundlesApi, type PromotionBundle } from '@/api/promotion-bundles'
import { useOrderCreateStore } from '@/stores/order-create'

export function ProductStep() {
  const {
    cartItems, addCartItem, removeCartItem, updateCartQuantity,
    bundleItems, addBundleItem, removeBundleItem, updateBundleQuantity,
    nextStep, prevStep,
  } = useOrderCreateStore()
  const [bundleKeyword, setBundleKeyword] = useState('')
  const [bundleSearchTerm, setBundleSearchTerm] = useState('')
  const [keyword, setKeyword] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<StoreProduct | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  // 促销组合搜索
  const { data: bundleData, isLoading: isBundleLoading } = useQuery({
    queryKey: ['settings', 'promotion-bundles', 'select', bundleSearchTerm],
    queryFn: () => promotionBundlesApi.select(bundleSearchTerm),
  })

  // 商品搜索
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'orders', 'products', searchTerm],
    queryFn: () => adminOrderApi.listProducts(searchTerm),
    enabled: searchTerm.length > 0,
  })

  // 商品详情（含 SKU）
  const { data: productDetail, isLoading: isDetailLoading } = useQuery({
    queryKey: ['admin', 'orders', 'products', 'detail', selectedProduct?.id],
    queryFn: () => adminOrderApi.getProductDetail(selectedProduct!.id),
    enabled: selectedProduct !== null && dialogOpen,
  })

  const handleBundleSearch = useCallback(() => {
    setBundleSearchTerm(bundleKeyword)
  }, [bundleKeyword])

  const handleSearch = useCallback(() => {
    setSearchTerm(keyword)
  }, [keyword])

  const handleBundleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleBundleSearch()
  }, [handleBundleSearch])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }, [handleSearch])

  const handleOpenProduct = useCallback((product: StoreProduct) => {
    setSelectedProduct(product)
    setDialogOpen(true)
  }, [])

  const handleAddSku = useCallback((detail: ProductDetail, skuIndex: number) => {
    const sku = detail.attr_value[skuIndex]
    if (!sku) return
    addCartItem({
      productId: detail.id,
      productName: detail.store_name,
      productImage: sku.image || detail.image,
      cartNum: 1,
      price: sku.price,
      unique: sku.unique,
      suk: sku.suk,
    })
    setDialogOpen(false)
    setSelectedProduct(null)
  }, [addCartItem])

  const handleAddBundle = useCallback((bundle: PromotionBundle) => {
    addBundleItem({
      bundleId: bundle.id,
      bundleName: bundle.name,
      quantity: 1,
      originalPrice: bundle.original_price,
      promotionPrice: bundle.promotion_price,
      items: bundle.items,
    })
  }, [addBundleItem])

  // 计算购物车总价
  const bundleTotal = bundleItems.reduce((sum, item) => sum + item.promotionPrice * item.quantity, 0)
  const bundleOriginalTotal = bundleItems.reduce((sum, item) => sum + item.originalPrice * item.quantity, 0)
  const bundleQuantity = bundleItems.reduce((sum, item) => sum + item.quantity, 0)
  const cartTotal = cartItems.reduce((sum, item) => sum + item.price * item.cartNum, 0)
  const cartQuantity = cartItems.reduce((sum, item) => sum + item.cartNum, 0)
  const orderTotal = bundleTotal + cartTotal
  const hasSelection = bundleItems.length > 0 || cartItems.length > 0

  const renderBundleSummary = (bundle: PromotionBundle | { items?: PromotionBundle['items'] }) => {
    const items = bundle.items || []
    if (items.length === 0) return '暂无组合商品明细'
    return items
      .slice(0, 3)
      .map((item) => `${item.product_name_snapshot}${item.sku_name_snapshot ? `/${item.sku_name_snapshot}` : ''} x${item.quantity}`)
      .join('、') + (items.length > 3 ? ` 等 ${items.length} 件` : '')
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        {/* 促销组合 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <PackageCheck className="h-4 w-4" />
              促销组合
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜索促销组合"
                  value={bundleKeyword}
                  onChange={(e) => setBundleKeyword(e.target.value)}
                  onKeyDown={handleBundleKeyDown}
                  className="pl-9"
                />
              </div>
              <Button onClick={handleBundleSearch}>
                搜索
              </Button>
            </div>

            <div className="space-y-2">
              {isBundleLoading && (
                <div className="text-center text-sm text-muted-foreground py-8 border rounded-lg">
                  加载中...
                </div>
              )}
              {!isBundleLoading && bundleData?.list?.length === 0 && (
                <div className="text-center text-sm text-muted-foreground py-8 border rounded-lg">
                  未找到匹配的促销组合
                </div>
              )}
              {bundleData?.list?.map((bundle) => (
                <div key={bundle.id} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{bundle.name}</p>
                      {bundle.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">{bundle.description}</p>
                      )}
                    </div>
                    <Button size="sm" onClick={() => handleAddBundle(bundle)}>
                      <Plus className="h-4 w-4 mr-1" />
                      添加
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {renderBundleSummary(bundle)}
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-bold text-red-600">¥{bundle.promotion_price.toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground line-through">¥{bundle.original_price.toFixed(2)}</span>
                    <span className="text-xs text-emerald-600">
                      省 ¥{Math.max(0, bundle.original_price - bundle.promotion_price).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm font-medium">
                <span>已选促销组合 ({bundleQuantity} 组)</span>
                <span className="text-red-600">¥{bundleTotal.toFixed(2)}</span>
              </div>
              {bundleItems.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8 border rounded-lg">
                  尚未选择促销组合
                </div>
              ) : (
                bundleItems.map((item, index) => (
                  <div key={item.bundleId} className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.bundleName}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {renderBundleSummary(item)}
                        </p>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        onClick={() => removeBundleItem(index)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm">
                        <span className="font-bold text-red-600">¥{item.promotionPrice.toFixed(2)}</span>
                        <span className="ml-2 text-xs text-muted-foreground line-through">¥{item.originalPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-7 w-7"
                          onClick={() => updateBundleQuantity(index, Math.max(1, item.quantity - 1))}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-7 w-7"
                          onClick={() => updateBundleQuantity(index, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* 普通商品 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShoppingCart className="h-4 w-4" />
              商品/普通商品
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜索商品名称"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="pl-9"
                />
              </div>
              <Button onClick={handleSearch} disabled={keyword.length === 0}>
                搜索
              </Button>
            </div>

            {/* 商品列表 */}
            {searchTerm && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">图片</TableHead>
                      <TableHead>商品名称</TableHead>
                      <TableHead className="w-24">价格</TableHead>
                      <TableHead className="w-20">库存</TableHead>
                      <TableHead className="w-24">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          加载中...
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading && data?.list?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          未找到匹配的商品
                        </TableCell>
                      </TableRow>
                    )}
                    {data?.list?.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <img
                            src={product.image}
                            alt={product.store_name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        </TableCell>
                        <TableCell className="font-medium max-w-[12rem] truncate">{product.store_name}</TableCell>
                        <TableCell className="text-red-600">¥{product.price}</TableCell>
                        <TableCell>{product.stock}</TableCell>
                        <TableCell>
                          <Button size="sm" onClick={() => handleOpenProduct(product)}>
                            <Plus className="h-4 w-4 mr-1" />
                            选规格
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <Separator />

            {/* 已选商品列表 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm font-medium">
                <span>已选商品 ({cartQuantity} 件)</span>
                <span className="text-red-600">¥{cartTotal.toFixed(2)}</span>
              </div>
              {cartItems.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8 border rounded-lg">
                  尚未选择普通商品
                </div>
              ) : (
                cartItems.map((item, index) => (
                  <div key={`${item.unique}-${index}`} className="flex items-center gap-3 p-2 border rounded-lg">
                    <img
                      src={item.productImage}
                      alt={item.productName}
                      className="h-10 w-10 rounded object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.productName}</p>
                      <p className="text-xs text-muted-foreground">{item.suk || '默认规格'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-600 font-medium">¥{item.price}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-7 w-7"
                          onClick={() => updateCartQuantity(index, Math.max(1, item.cartNum - 1))}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center text-sm">{item.cartNum}</span>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-7 w-7"
                          onClick={() => updateCartQuantity(index, item.cartNum + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        onClick={() => removeCartItem(index)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SKU 选择弹窗 */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setSelectedProduct(null) }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>选择规格</DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.store_name}
                  className="h-16 w-16 rounded object-cover"
                />
                <div>
                  <p className="font-medium">{selectedProduct.store_name}</p>
                  <p className="text-red-600 text-lg font-bold">¥{selectedProduct.price}</p>
                </div>
              </div>
              <Separator />
              {isDetailLoading ? (
                <p className="text-center text-muted-foreground py-4">加载规格中...</p>
              ) : productDetail?.attr_value && productDetail.attr_value.length > 0 ? (
                <div className="space-y-2">
                  {productDetail.attr_value.map((sku, index) => (
                    <div
                      key={sku.unique}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer"
                      onClick={() => handleAddSku(productDetail, index)}
                    >
                      <div className="flex items-center gap-3">
                        {sku.image && (
                          <img src={sku.image} alt={sku.suk} className="h-8 w-8 rounded object-cover" />
                        )}
                        <div>
                          <p className="font-medium">{sku.suk || '默认'}</p>
                          <p className="text-sm text-muted-foreground">库存: {sku.stock}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-red-600 font-bold">¥{sku.price}</p>
                        <Button size="sm" variant="outline">
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-3">该商品无规格</p>
                  <Button onClick={() => handleAddSku({ ...selectedProduct, attr_value: [] } as ProductDetail, -1)}>
                    此处无 SKU，直接添加
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            促销组合 <span className="font-medium text-foreground">{bundleQuantity}</span> 组
            <span className="mx-2">+</span>
            普通商品 <span className="font-medium text-foreground">{cartQuantity}</span> 件
            {bundleOriginalTotal > bundleTotal && (
              <span className="ml-3 text-emerald-600">
                组合优惠 ¥{(bundleOriginalTotal - bundleTotal).toFixed(2)}
              </span>
            )}
          </div>
          <div className="text-base font-bold">
            总额 <span className="text-red-600">¥{orderTotal.toFixed(2)}</span>
          </div>
        </CardContent>
      </Card>

      {/* 底部操作栏 */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={prevStep}>上一步</Button>
        <Button onClick={nextStep} disabled={!hasSelection}>
          下一步
        </Button>
      </div>
    </div>
  )
}
